<script lang="ts" setup>
/*
 * The design system's canonical Toast card uses a left accent bar per tone;
 * this library colors a whole toast (background, border, text) per type
 * instead, with no equivalent hook to add a bar without fighting its own
 * internals, so the style block below reconciles tone through that theming
 * surface rather than reproducing the accent bar (derived). Its own type
 * system also has no brand tone, so a brand toast falls back to neutral
 * styling the same way an unstyled one already does.
 *
 * Keep that block's own CSS custom property declarations free of prose
 * comments containing sequences shaped like one (a leading double hyphen):
 * this repo's nested-selector preprocessor corrupts them, which surfaces as
 * a PostCSS "Unknown word" build error pointing at an unrelated line.
 */
import type { ToasterProps } from 'vue-sonner'
import { reactiveOmit } from '@vueuse/core'
import { CircleCheck, Info, Loader2, OctagonX, TriangleAlert, X } from '@lucide/vue'
import { Toaster as Sonner } from 'vue-sonner'

const props = defineProps<ToasterProps>()
const delegatedProps = reactiveOmit(props, 'toastOptions')
</script>

<template>
  <Sonner
    class="toaster group font-ds"
    :toast-options="{
      classes: {
        toast: 'group toast group-[.toaster]:shadow-ds-lg !border-0 !border-l-2',
        title: 'group-[.toast]:font-bold',
        description: 'group-[.toast]:text-sm group-[.toast]:font-medium',
        actionButton:
          'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground group-[.toast]:rounded-ds-sm',
        cancelButton:
          'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground group-[.toast]:rounded-ds-sm',
      },
    }"
    v-bind="delegatedProps"
    rich-colors
  >
    <template #success-icon>
      <CircleCheck class="size-4" />
    </template>
    <template #info-icon>
      <Info class="size-4" />
    </template>
    <template #warning-icon>
      <TriangleAlert class="size-4" />
    </template>
    <template #error-icon>
      <OctagonX class="size-4" />
    </template>
    <template #loading-icon>
      <Loader2 class="size-4 animate-spin" />
    </template>
    <template #close-icon>
      <X class="size-4" />
    </template>
  </Sonner>
</template>

<style>
/*
 * A higher-specificity selector than sonner's own base rule, which sets its
 * defaults on [data-sonner-toaster][data-sonner-theme=light|dark] (two
 * attribute selectors) — a plain .toaster class alone ties that and loses
 * on import order. Three selectors here beats it outright, so this wins
 * regardless of where vue-sonner's own stylesheet lands in the cascade.
 */
.toaster[data-sonner-toaster][data-sonner-theme] {
  --normal-bg: rgb(var(--ds-card));
  --normal-border: rgb(var(--ds-border));
  --normal-text: rgb(var(--ds-foreground));
  --success-bg: rgb(var(--ds-success-subtle));
  --success-border: rgb(var(--ds-success));
  --success-text: rgb(var(--ds-success-subtle-foreground));
  --error-bg: rgb(var(--ds-destructive-subtle));
  --error-border: rgb(var(--ds-destructive));
  --error-text: rgb(var(--ds-destructive-subtle-foreground));
  --warning-bg: rgb(var(--ds-warning-subtle));
  --warning-border: rgb(var(--ds-warning));
  --warning-text: rgb(var(--ds-warning-subtle-foreground));
  --info-bg: rgb(var(--ds-info-subtle));
  --info-border: rgb(var(--ds-info));
  --info-text: rgb(var(--ds-info-subtle-foreground));
  --border-radius: var(--ds-radius-md);
}
</style>
